import type {ProjectDocumentNs} from '@shared/types';

/*
 * Single owner of how a document's server-derived analysis state is shown, so
 * no component infers state from the presence of `contentSummary` (wrong for
 * pending/failed attempts and legacy summaries). Missing status → CONVERTED
 * (older backend); unknown value → neutral, never analyzed or retryable; failure
 * copy comes only from the bounded error-code allow-list.
 */

const KNOWN_STATUSES: ReadonlySet<string> = new Set<string>([
  'CONVERTED',
  'ANALYSIS_PENDING',
  'ANALYZING',
  'ANALYZED',
  'ANALYSIS_FAILED',
  'SUPERSEDED',
]);

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  'PROVIDER_UNAVAILABLE',
  'PROVIDER_ERROR',
  'INVALID_OUTPUT',
  'TIMEOUT',
  'INPUT_TOO_LARGE',
  'CANCELLED',
  'INTERNAL_ERROR',
]);

/** Sentinel for values the client does not know (forward compatibility). */
export const UNKNOWN_ANALYSIS_STATUS = 'UNKNOWN' as const;

export type ResolvedAnalysisStatus =
  | ProjectDocumentNs.AnalysisStatus
  | typeof UNKNOWN_ANALYSIS_STATUS;

export type AnalysisStatusTone = 'processing' | 'success' | 'danger' | 'neutral';

export interface AnalysisStatusPresentation {
  status: ResolvedAnalysisStatus;
  /** Short badge label. */
  label: string;
  /** Visual tone for the badge. */
  tone: AnalysisStatusTone;
  /** A pending or running attempt exists; actions must stay disabled. */
  isActive: boolean;
  /** The latest attempt succeeded. */
  isAnalyzed: boolean;
  /** Only ANALYSIS_FAILED may offer "Retry". */
  isRetryable: boolean;
  /** CONVERTED or ANALYSIS_FAILED: an analyze request may target this document. */
  canRequestAnalysis: boolean;
  /** Safe, user-facing failure description; empty unless failed. */
  errorMessage: string;
}

type StatusSource = Pick<ProjectDocumentNs.ProjectDocument, 'analysisStatus' | 'analysisErrorCode'> | null | undefined;

/** Normalizes the wire value: missing → CONVERTED, unexpected → UNKNOWN. */
export function resolveAnalysisStatus(doc: StatusSource): ResolvedAnalysisStatus {
  const raw = doc?.analysisStatus;
  if (raw === undefined || raw === null) return 'CONVERTED';
  if (typeof raw === 'string' && KNOWN_STATUSES.has(raw)) {
    return raw as ProjectDocumentNs.AnalysisStatus;
  }
  return UNKNOWN_ANALYSIS_STATUS;
}

/** Bounded error code → safe copy. Unknown/absent codes fall back to generic text. */
export function describeAnalysisError(code: string | null | undefined): string {
  switch (code) {
    case 'PROVIDER_UNAVAILABLE': return 'The analysis service was unavailable. Please retry.';
    case 'PROVIDER_ERROR':       return 'The analysis service returned an error. Please retry.';
    case 'INVALID_OUTPUT':       return 'The analysis result could not be read. Please retry.';
    case 'TIMEOUT':              return 'The analysis timed out. Please retry.';
    case 'INPUT_TOO_LARGE':      return 'The document is too large to analyze.';
    case 'CANCELLED':            return 'The analysis was cancelled.';
    case 'INTERNAL_ERROR':       return 'An internal error interrupted the analysis. Please retry.';
    default:                     return 'The analysis failed. Please retry.';
  }
}

export function getAnalysisPresentation(doc: StatusSource): AnalysisStatusPresentation {
  const status = resolveAnalysisStatus(doc);
  const base = {
    status,
    isActive: false,
    isAnalyzed: false,
    isRetryable: false,
    canRequestAnalysis: false,
    errorMessage: '',
  };

  switch (status) {
    case 'CONVERTED':
      return {...base, label: 'Ready to analyze', tone: 'neutral', canRequestAnalysis: true};
    case 'ANALYSIS_PENDING':
    case 'ANALYZING':
      return {...base, label: 'Processing', tone: 'processing', isActive: true};
    case 'ANALYZED':
      return {...base, label: 'Analyzed', tone: 'success', isAnalyzed: true};
    case 'ANALYSIS_FAILED': {
      const code = doc?.analysisErrorCode;
      const safeCode = typeof code === 'string' && KNOWN_ERROR_CODES.has(code) ? code : null;
      return {
        ...base,
        label: 'Analysis failed',
        tone: 'danger',
        isRetryable: true,
        canRequestAnalysis: true,
        errorMessage: describeAnalysisError(safeCode),
      };
    }
    case 'SUPERSEDED':
      // Kept for exhaustive handling; the backend does not emit it for a
      // document's latest projection. Shown neutrally, no actions.
      return {...base, label: 'Superseded', tone: 'neutral'};
    default:
      return {...base, label: 'Status unavailable', tone: 'neutral'};
  }
}

export function isDocumentAnalyzed(doc: StatusSource): boolean {
  return getAnalysisPresentation(doc).isAnalyzed;
}

export function isDocumentAnalysisActive(doc: StatusSource): boolean {
  return getAnalysisPresentation(doc).isActive;
}

export function isDocumentAnalysisRetryable(doc: StatusSource): boolean {
  return getAnalysisPresentation(doc).isRetryable;
}

/** CONVERTED or ANALYSIS_FAILED — never active/analyzed/unknown. */
export function canRequestDocumentAnalysis(doc: StatusSource): boolean {
  return getAnalysisPresentation(doc).canRequestAnalysis;
}

// ─── Context acceptance (held-document decision flow) ────────────────────────

export type ContextAcceptanceTone = 'warning' | 'success' | 'neutral';

export interface ContextAcceptancePresentation {
  label: string;
  tone: ContextAcceptanceTone;
}

type AcceptanceSource = Pick<ProjectDocumentNs.ProjectDocument, 'contextAcceptance'> | null | undefined;

/**
 * Badge copy for whether a document's content made it into the project
 * context. `null` for NOT_ASSESSED / missing / unknown values — those rows
 * show no extra badge (older backends never send the field).
 */
export function getContextAcceptancePresentation(doc: AcceptanceSource): ContextAcceptancePresentation | null {
  switch (doc?.contextAcceptance) {
    case 'PENDING_DECISION': return {label: 'Uploaded, not added to project', tone: 'warning'};
    case 'REJECTED':         return {label: 'Not added to project', tone: 'neutral'};
    case 'ACCEPTED':         return {label: 'Added to project', tone: 'success'};
    default:                 return null;
  }
}

/** PENDING_DECISION or REJECTED: the content exists but is not part of the project context. */
export function isDocumentHeldOut(doc: AcceptanceSource): boolean {
  const a = doc?.contextAcceptance;
  return a === 'PENDING_DECISION' || a === 'REJECTED';
}
